import { Box, ClickAwayListener, Fade, Typography } from '@mui/material';
import { ReactNode, useEffect, useRef, useState } from 'react';

import { StyledPopper, StyledTooltip } from './TooltipStyles';

import { createTooltipModifiers } from '../lib/tooltipModifiers';
import { useTooltipHandlers } from '../lib/useTooltipHandlers';

export interface DynamicTooltipProps {
    content: string | ReactNode;
    children: ReactNode;
    maxWidth?: number;
    disabled?: boolean;
    enterDelay?: number;
    leaveDelay?: number;
    className?: string;
    containerStyle?: React.CSSProperties;
    'data-testid'?: string;
    trigger?: 'hover' | 'click';
    offset?: [number, number]; // [horizontal, vertical] offset in pixels
    interactive?: boolean;
}

export const DynamicTooltip = ({
    content,
    children,
    maxWidth = 500,
    disabled = false,
    enterDelay = 200,
    leaveDelay,
    className,
    containerStyle,
    'data-testid': dataTestId,
    trigger = 'hover',
    offset = [0, -4],
    interactive = false,
}: DynamicTooltipProps) => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLElement>(null);

    const leaveDelayResolved =
        interactive && leaveDelay === undefined ? 300 : leaveDelay ?? 0;

    const {
        handleMouseEnter,
        handleMouseLeave,
        handlePopperMouseEnter,
        handlePopperMouseLeave,
        handleClick,
        handleClickAway,
        clearTimeout,
    } = useTooltipHandlers({
        disabled,
        trigger,
        enterDelay,
        leaveDelay: leaveDelayResolved,
        onOpenChange: setOpen,
    });

    useEffect(() => {
        if (disabled && open) {
            setOpen(false);
            clearTimeout();
        }
    }, [disabled, open, clearTimeout]);

    const renderContent = () => {
        if (typeof content === 'string') {
            return (
                <div style={{ maxWidth }}>
                    <Typography
                        variant="body2"
                        component="span"
                        sx={{ whiteSpace: 'pre-wrap', lineHeight: '1.4' }}
                    >
                        {content}
                    </Typography>
                </div>
            );
        }
        return content;
    };

    if (disabled) {
        return <>{children}</>;
    }

    return (
        <>
            <span
                ref={anchorRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleClick();
                    }
                }}
                style={{
                    display: 'inline',
                    ...(typeof containerStyle === 'object'
                        ? containerStyle
                        : {}),
                }}
                className={className}
                data-testid={dataTestId}
                role="button"
                aria-describedby={open ? 'tooltip-content' : undefined}
            >
                {children}
            </span>

            <StyledPopper
                open={open}
                anchorEl={anchorRef.current}
                placement="top"
                modifiers={createTooltipModifiers(offset)}
                transition
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={200}>
                        <Box>
                            <StyledTooltip>
                                <ClickAwayListener
                                    onClickAway={handleClickAway}
                                >
                                    <div
                                        id="tooltip-content"
                                        onMouseEnter={
                                            interactive
                                                ? handlePopperMouseEnter
                                                : undefined
                                        }
                                        onMouseLeave={
                                            interactive
                                                ? handlePopperMouseLeave
                                                : undefined
                                        }
                                        onClick={
                                            interactive
                                                ? () => setOpen(false)
                                                : undefined
                                        }
                                    >
                                        {renderContent()}
                                    </div>
                                </ClickAwayListener>
                            </StyledTooltip>
                            <div className="tooltip-arrow" data-popper-arrow />
                            <div className="tooltip-arrow-shadow" />
                        </Box>
                    </Fade>
                )}
            </StyledPopper>
        </>
    );
};
